import { GetPublishedSubjectListResponseData } from "./../response/get-published-subject-list-response-data";
import { HttpResponse } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";

import { Observable } from "rxjs";

import {
    IHttpRequestObserveResponseOptions,
    ServerGatewayBase
} from "@logex/framework/lg-application";

import {
    CreateSubjectResponseData,
    DeleteSubjectResponseData,
    GetPermissionsResponseData,
    GetExternalOptionsetResponseData,
    GetSubjectResponseData,
    GetSubjectListResponseData,
    LockResponseData,
    UpdateExternalOptionsetResponseData,
    DeleteExternalOptionsetResponseData,
    GetScmResponseData,
    CopySubjectResponseData,
    PostSubjectMergeChangesResponseData,
    AuthConfiguration,
    FilterOptionsData,
    GetPublishedPatientResponseData,
    GetProjectListResponseData,
    GetOwnerIdsResponseData
} from "../response";

import { SubjectListQuery } from "./fhir-middleware-api.types";
import { UpdateVariableResponseData } from "../response/update-variable-response-data";
import { FHIR_MIDDLEWARE_SERVICE_API_URL } from "./fhir";
import { FetchSubjectResponseData } from "../response/fetch-subject-response-data";
import { FormConfiguration } from "../response/get-form-configuration-response-data";
import { PublishSubjectResponseData } from "../response/publish-subject-response-data";
import { GetReviewChangesResponseData } from "../response/get-reviews-changes-response-data";
import { QualityRule } from "@logex/expression-validator/src/lib/types/quality-rule.type";
import { CopySubjectRequestBody, PublishSubjectRequestBody } from "../request";
import { AuthorizationOrganization } from "@authorization";
interface SettableHttpOptions<T = any> {
    organization: AuthorizationOrganization;
    contentType?: string;
    accept?: string;
    responseType?: "text" | "json" | "arraybuffer" | "blob" | undefined;
    queryParams?: Record<string, any> | null;
    body?: T;
}

export interface UpdateVariablesRequestVariables {
    [VariableName: string]: string;
}

@Injectable({ providedIn: "root" })
export class FHIRMiddlewareApiService extends ServerGatewayBase {
    baseUrl: string = inject<string>(FHIR_MIDDLEWARE_SERVICE_API_URL);

    constructor() {
        super();
        this._setBaseUrl(this.baseUrl);
    }

    getImageData$(
        organization: AuthorizationOrganization,
        projectName: string,
        imageName: string
    ): Observable<HttpResponse<string>> {
        const httpOptions = this._getHttpOptions({
            organization,
            responseType: "text"
        });
        return this._get<string>(`/${projectName}/images/${imageName}`, httpOptions);
    }

    getProjectList$(): Observable<GetProjectListResponseData> {
        return this._get<GetProjectListResponseData>("/projectlist");
    }

    getProjectPermissions$(
        organization: AuthorizationOrganization,
        projectName: string
    ): Observable<HttpResponse<GetPermissionsResponseData>> {
        const httpOptions = this._getHttpOptions({ organization });
        return this._get<GetPermissionsResponseData>(`/${projectName}/permissions`, httpOptions);
    }

    getFormConfiguration$(
        organization: AuthorizationOrganization,
        projectName: string
    ): Observable<HttpResponse<FormConfiguration>> {
        const httpOptions = this._getHttpOptions({ organization });
        return this._get<FormConfiguration>(`/${projectName}/form-configuration`, httpOptions);
    }

    getValidationRules$(
        organization: AuthorizationOrganization,
        projectName: string
    ): Observable<HttpResponse<QualityRule[]>> {
        const httpOptions = this._getHttpOptions({ organization });
        return this._get<QualityRule[]>(`/${projectName}/validation-configuration`, httpOptions);
    }

    getScm$(
        organization: AuthorizationOrganization,
        projectName: string
    ): Observable<HttpResponse<GetScmResponseData>> {
        const httpOptions = this._getHttpOptions({ organization });
        /**return of(
            new HttpResponse<GetScmResponseData>({
                body: structuredClone(GET_SCM_RESPONSE_TESTDATA)
            })
        );**/
        return this._get<GetScmResponseData>(`/${projectName}/scm`, httpOptions);
    }

    getAuthConfiguration$(
        organization: AuthorizationOrganization,
        projectName: string
    ): Observable<HttpResponse<AuthConfiguration>> {
        const httpOptions = this._getHttpOptions({ organization });
        return this._get<AuthConfiguration>(`/${projectName}/auth-configuration`, httpOptions);
    }

    getOwnerIds$(
        organization: AuthorizationOrganization,
        projectName: string
    ): Observable<HttpResponse<GetOwnerIdsResponseData>> {
        const httpOptions = this._getHttpOptions({ organization });
        return this._get<GetOwnerIdsResponseData>(
            `/${projectName}/registryAdditionalInfo`,
            httpOptions
        );
    }

    getSubjectList$(
        organization: AuthorizationOrganization,
        projectName: string,
        { pageNumber, pageSize, columns, sortBy, sortType, searchCriteria }: SubjectListQuery
    ): Observable<HttpResponse<GetSubjectListResponseData>> {
        const httpOptions = this._getHttpOptions({
            organization,
            queryParams: {
                pageNumber,
                pageSize,
                columns,
                sortBy,
                sortType,
                searchCriteria
            }
        });
        return this._get<GetSubjectListResponseData>(`/${projectName}/subjectlist`, httpOptions);
    }

    getPublishedList$(
        organization: AuthorizationOrganization,
        projectName: string,
        { pageNumber, pageSize, columns, sortBy, sortType, searchCriteria }: SubjectListQuery
    ): Observable<HttpResponse<GetPublishedSubjectListResponseData>> {
        const httpOptions = this._getHttpOptions({
            organization,
            queryParams: {
                pageNumber,
                pageSize,
                columns,
                sortBy,
                sortType,
                searchCriteria
            }
        });
        return this._get<GetPublishedSubjectListResponseData>(
            `/${projectName}/publishedList`,
            httpOptions
        );
    }

    getFilterOptions$(
        organization: AuthorizationOrganization,
        projectName: string,
        variableName: string
    ): Observable<HttpResponse<FilterOptionsData>> {
        const httpOptions = this._getHttpOptions({ organization, queryParams: { variableName } });
        return this._get<FilterOptionsData>(`/${projectName}/filterOptions`, httpOptions);
    }

    publishSubject$(
        organization: AuthorizationOrganization,
        projectName: string,
        rootDatasetName: string,
        subjectId: string,
        version: string,
        expressionValidatorVersion: string
    ): Observable<HttpResponse<PublishSubjectResponseData>> {
        const httpOptions = this._getHttpOptions<PublishSubjectRequestBody>({
            organization,
            body: { version, expressionValidatorVersion },
            contentType: "application/json"
        });
        return this._post<PublishSubjectResponseData>(
            `/${projectName}/${rootDatasetName}/save/${subjectId}`,
            httpOptions
        );
    }

    getSubject$(
        organization: AuthorizationOrganization,
        projectName: string,
        rootDatasetName: string,
        subjectName: string
    ): Observable<HttpResponse<GetSubjectResponseData>> {
        const httpOptions = this._getHttpOptions({ organization });
        return this._get<GetSubjectResponseData>(
            `/${projectName}/${rootDatasetName}/${subjectName}`,
            httpOptions
        );
    }

    postSubjectMergeChanges$(
        organization: AuthorizationOrganization,
        projectName: string,
        rootDatasetName: string,
        subjectName: string,
        etag?: string
    ): Observable<HttpResponse<PostSubjectMergeChangesResponseData>> {
        const httpOptions = this._getHttpOptions<string | undefined>({
            organization,
            contentType: "application/json-patch+json",
            body: etag
        });
        return this._post<PostSubjectMergeChangesResponseData>(
            `/${projectName}/${rootDatasetName}/${subjectName}/merge-changes`,
            httpOptions
        );
    }

    getSubjectReviewChanges$(
        organization: AuthorizationOrganization,
        projectName: string,
        rootDatasetName: string,
        subjectName: string
    ): Observable<HttpResponse<GetReviewChangesResponseData>> {
        const httpOptions = this._getHttpOptions({
            organization
        });
        return this._get<GetReviewChangesResponseData>(
            `/${projectName}/${rootDatasetName}/${subjectName}/review-changes`,
            httpOptions
        );
    }

    copySubject$(
        organization: AuthorizationOrganization,
        projectName: string,
        datasetName: string,
        subjectName: string,
        parentSubjectId?: string,
        etag?: string,
        initialValues: Record<string, string> = {},
        excludedFields: string[] = []
    ): Observable<HttpResponse<CopySubjectResponseData>> {
        const httpOptions = this._getHttpOptions<CopySubjectRequestBody>({
            organization,
            body: {
                etag,
                data: initialValues,
                excludedFields,
                parentSubjectId
            },
            contentType: "application/json"
        });
        return this._post<CopySubjectResponseData>(
            `/${projectName}/${datasetName}/${subjectName}/copy`,
            httpOptions
        );
    }

    createSubject$(
        organization: AuthorizationOrganization,
        projectName: string,
        rootDatasetName: string,
        variablevalues: Record<string, string>
    ): Observable<HttpResponse<CreateSubjectResponseData>> {
        const httpOptions = this._getHttpOptions({
            organization,
            body: variablevalues,
            contentType: "application/json"
        });
        return this._post<CreateSubjectResponseData>(
            `/${projectName}/${rootDatasetName}`,
            httpOptions
        );
    }

    fetchSubject$(
        organization: AuthorizationOrganization,
        projectName: string,
        variablevalues: Record<string, string>
    ): Observable<HttpResponse<FetchSubjectResponseData>> {
        const httpOptions = this._getHttpOptions({
            organization,
            body: variablevalues,
            contentType: "application/json"
        });
        return this._post<FetchSubjectResponseData>(`/${projectName}/fetch`, httpOptions);
    }

    createSubjectChild$(
        organization: AuthorizationOrganization,
        projectName: string,
        datasetApiPath: string,
        subjectApiPath: string,
        variablevalues: Record<string, string>,
        etag: string
    ): Observable<HttpResponse<CreateSubjectResponseData>> {
        const httpOptions = this._getHttpOptions({
            organization,
            body: {
                initialValues: variablevalues,
                etag
            },
            contentType: "application/json"
        });
        return this._post<CreateSubjectResponseData>(
            `/${projectName}/${datasetApiPath}/${subjectApiPath}/`,
            httpOptions
        );
    }

    deleteSubject$(
        organization: AuthorizationOrganization,
        projectName: string,
        datasetApiPath: string,
        subjectApiPath: string,
        deletePublished: boolean = false,
        { etag }: { etag: string }
    ): Observable<HttpResponse<DeleteSubjectResponseData>> {
        const httpOptions = this._getHttpOptions({
            organization,
            contentType: "application/json",
            body: etag,
            queryParams: { IncludePublished: deletePublished ? "true" : "false" }
        });
        return this._delete<DeleteSubjectResponseData>(
            `/${projectName}/${datasetApiPath}/${subjectApiPath}/`,
            httpOptions
        );
    }

    updateVariable$(
        organization: AuthorizationOrganization,
        projectName: string,
        datasetApiPath: string,
        subjectApiPath: string,
        variableName: string,
        variableValue: string,
        { etag }: { etag: string }
    ): Observable<HttpResponse<UpdateVariableResponseData>> {
        const httpOptions = this._getHttpOptions({
            organization,
            contentType: "application/json-patch+json",
            body: { value: variableValue, etag }
        });

        return this._put<UpdateVariableResponseData>(
            `/${projectName}/${datasetApiPath}/${subjectApiPath}/${variableName}`,
            httpOptions
        );
    }

    updateVariables$(
        organization: AuthorizationOrganization,
        projectName: string,
        datasetApiPath: string,
        subjectApiPath: string,
        variables: UpdateVariablesRequestVariables,
        { etag }: { etag: string }
    ): Observable<HttpResponse<UpdateVariableResponseData>> {
        const httpOptions = this._getHttpOptions({
            organization,
            contentType: "application/json-patch+json",
            body: { variables, etag }
        });

        return this._put<UpdateVariableResponseData>(
            `/${projectName}/${datasetApiPath}/${subjectApiPath}`,
            httpOptions
        );
    }

    lock$(
        organization: AuthorizationOrganization,
        projectName: string,
        datasetName: string,
        subjectName: string,
        etag: string
    ): Observable<HttpResponse<LockResponseData>> {
        const httpOptions = this._getHttpOptions({
            organization,
            contentType: "application/json-patch+json",
            body: etag
        });
        return this._post<LockResponseData>(
            `/${projectName}/${datasetName}/${subjectName}/lock`,
            httpOptions
        );
    }

    unlock$(
        organization: AuthorizationOrganization,
        projectName: string,
        datasetName: string,
        subjectName: string,
        etag: string
    ): Observable<HttpResponse<void>> {
        const httpOptions = this._getHttpOptions({
            organization,
            body: etag,
            contentType: "application/json-patch+json"
        });

        return this._post<void>(
            `/${projectName}/${datasetName}/${subjectName}/unlock`,
            httpOptions
        );
    }

    getPublishedPatient$(
        organization: AuthorizationOrganization,
        projectName: string,
        datasetName: string,
        naturalKeys: Record<string, string>,
        fields: string[]
    ): Observable<HttpResponse<GetPublishedPatientResponseData>> {
        const httpOptions = this._getHttpOptions({
            organization,
            queryParams: {
                keys: naturalKeys,
                fields
            }
        });
        return this._get<GetPublishedPatientResponseData>(
            `/${projectName}/published-patient/dataset/${datasetName}`,
            httpOptions
        );
    }

    exportValidationErrors$(
        organization: AuthorizationOrganization,
        projectName: string,
        subjectIds: string[],
        language: string
    ): Observable<HttpResponse<Blob>> {
        const httpOptions = this._getHttpOptions({
            organization,
            contentType: "application/json",
            accept: "application/octet-stream",
            responseType: "blob",
            body: { subjectIds, language }
        });
        return this._post<Blob>(
            `/${projectName}/patient/validation-errors/export`,
            httpOptions
        );
    }

    getExternalOptionset$(
        organization: AuthorizationOrganization,
        projectName: string,
        name: string,
        searchCriteria?: Record<string, string>
    ): Observable<HttpResponse<GetExternalOptionsetResponseData>> {
        const httpOptions = this._getHttpOptions({
            organization,
            queryParams: { searchCriteria }
        });

        return this._get<GetExternalOptionsetResponseData>(
            `/${projectName}/external-optionset/${name}`,
            httpOptions
        );
    }

    updateExternalOptionset$(
        organization: AuthorizationOrganization,
        projectName: string,
        datasetApiPath: string,
        subjectApiPath: string,
        variableName: string,
        { rowId, etag }: { rowId: string; etag: string }
    ): Observable<HttpResponse<UpdateExternalOptionsetResponseData>> {
        const httpOptions = this._getHttpOptions({
            organization,
            contentType: "application/json-patch+json",
            body: { variable: variableName, dataRowId: rowId, etag }
        });

        return this._put<UpdateExternalOptionsetResponseData>(
            `/${projectName}/${datasetApiPath}/${subjectApiPath}/externalOptionset`,
            httpOptions
        );
    }

    deleteExternalOptionset$(
        organization: AuthorizationOrganization,
        projectName: string,
        datasetApiPath: string,
        subjectApiPath: string,
        { variableName, etag }: { variableName: string; etag: string }
    ): Observable<HttpResponse<DeleteExternalOptionsetResponseData>> {
        const httpOptions = this._getHttpOptions({
            organization,
            contentType: "application/json-patch+json",
            body: { variable: variableName, etag }
        });

        return this._delete<DeleteExternalOptionsetResponseData>(
            `/${projectName}/${datasetApiPath}/${subjectApiPath}/externalOptionset`,
            httpOptions
        );
    }

    private _getHttpOptions<T = any>({
        organization,
        contentType,
        accept,
        responseType,
        queryParams = null,
        body
    }: SettableHttpOptions<T>): IHttpRequestObserveResponseOptions {
        const httpOptions: IHttpRequestObserveResponseOptions = {
            observe: "response"
        };

        httpOptions.headers = {};
        httpOptions.headers["x-organization-id"] = String(organization.organizationId);
        httpOptions.headers["x-organization-cic"] = String(organization.cicCode);

        if (contentType) {
            httpOptions.headers["content-type"] = contentType;
        }

        if (accept) {
            httpOptions.headers["accept"] = accept;
        }

        if (responseType) {
            httpOptions.responseType = responseType;
        }

        if (queryParams) {
            httpOptions.params = {};
            for (const name of Object.keys(queryParams)) {
                if (typeof queryParams[name] !== "undefined") {
                    if (Array.isArray(queryParams[name])) {
                        httpOptions.params[name] = queryParams[name];
                    } else if (
                        Object.prototype.toString.call(queryParams[name]) === "[object Object]"
                    ) {
                        for (const key of Object.keys(queryParams[name])) {
                            if (typeof queryParams[name][key] !== "undefined") {
                                httpOptions.params[`${name}[${key}]`] = String(
                                    queryParams[name][key]
                                );
                            }
                        }
                    } else {
                        httpOptions.params[name] = String(queryParams[name]);
                    }
                }
            }
        }

        httpOptions.body = body ? JSON.stringify(body) : "";

        return httpOptions;
    }
}
